import { keepPreviousData, useQuery, UseQueryResult } from '@tanstack/react-query';
import { api } from 'src/constants/apiRoutes';
import { queryKeys } from 'src/constants/querykeys';
import { GetProductsResponse, Product, ProductListFilterRequest } from 'src/types/catalog/Product';
import axiosInstance from 'src/utils/axios';
import { getProductImageSrc, parseProductImageUrl } from 'src/utils/parseProductImageUrl';

interface useGetProductsQueryOptions {
  request: ProductListFilterRequest;
  searchText?: string;
  enabled?: boolean;
}

async function fetchProducts(
  request: ProductListFilterRequest,
  searchText?: string
): Promise<GetProductsResponse> {
  const url = searchText
    ? `${api.getProducts}?searchText=${encodeURIComponent(searchText)}`
    : api.getProducts;
  const response = await axiosInstance.post<GetProductsResponse>(url, request);
  return response.data;
}

export const useGetProductsQuery = (
  options: useGetProductsQueryOptions
): UseQueryResult<GetProductsResponse> => {
  const { request, searchText, enabled } = options;

  const queryKey: readonly unknown[] = queryKeys.product.productList({
    ...request,
    searchText,
  });

  return useQuery<GetProductsResponse>({
    queryKey,
    queryFn: () => fetchProducts(request, searchText),
    enabled: enabled !== undefined ? enabled : request.catalogId > 0, // Use provided enabled or default to catalogId > 0
    placeholderData: keepPreviousData,
    select: (data) => ({
      ...data,
      items: data.items.map((item: Product) => {
        // Transform the URL using the parseProductImageUrl function
        const transformedUrl = getProductImageSrc(item.url as string | null | undefined, {
          productDescription: item.description,
          viewType: 'advanced',
        });
        const parsedUrl = parseProductImageUrl(item.url as string | null | undefined);

        return {
          ...item,
          productKey: `${item.catalogId}-${item.categoryId}-${item.productId}`,
          price: parseFloat(item.price as string) || 0,
          url: transformedUrl,
          attributes: [
            item.isContracted ? 'isContracted' : null,
            item.productInFavoriteFolder ? 'productInFavoriteFolder' : null,
            item.isFormulary ? 'isFormulary' : null,
            item.isImageAvailable ? 'isImageAvailable' : null,
            item.isSpecialOrder ? 'isSpecialOrder' : null,
            item.isOfflineSupplierItem ? 'isOfflineSupplierItem' : null,
          ].filter((attribute): attribute is string => attribute !== null),
          ...(parsedUrl as any),
        };
      }),
    }),
  });
};
